/**
 * N8N-style "+" / trash hover affordance for ReactFlow edges.
 *
 * Two pieces in one file because they're tightly coupled:
 *
 *   1. ``TrayActionsEdge`` — the custom Edge component. Renders the
 *      bezier path same as the default, then floats a small action
 *      group at the midpoint via ``EdgeLabelRenderer``:
 *        * "+" button — opens the palette popover (caller wires
 *          the click handler via ``data.onInsertClick``)
 *        * trash button — deletes the edge (``data.onDelete``)
 *      Both fade in on hover; idle edges show neither so the canvas
 *      stays clean.
 *
 *   2. ``PalettePopover`` — anchorless floating popover the parent
 *      mounts at the click position. Re-uses the same catalog/filter
 *      logic as ``NodePalette`` but as a categorised inline list with
 *      a search box. On selection, calls back with the picked kind.
 *
 * Why one file: the edge and the popover are useless without each
 * other, and splitting them would mean threading three props down
 * through ReactFlow's ``edgeTypes`` map instead of one.
 */
import { Plus, Trash2 } from "lucide-react";
import * as React from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from "reactflow";

import { NodeIconBadge } from "@/components/workflows/nodeIcons";
import { Input } from "@/components/ui/input";
import type {
  CatalogNode,
  Direction,
  NodeKind,
  WorkflowKind,
} from "@/lib/api-workflows";
import { cn } from "@/lib/cn";


// ---------------------------------------------------------------------------
// Custom edge data
// ---------------------------------------------------------------------------


export interface TrayEdgeActionsData {
  /** Fired when the user clicks "+" on the edge. The parent opens the
   *  PalettePopover at ``screenPos`` and, on selection, calls
   *  ``insertOnEdge(edgeId, kind)``. */
  onInsertClick?: (edgeId: string, screenPos: { x: number; y: number }) => void;
  /** Fired when the user clicks the trash icon on the edge. */
  onDelete?: (edgeId: string) => void;
  /** n8n-style live label: number of items that flowed down this edge
   *  during the run being shown. Rendered as a small pill on the edge.
   *  Undefined when no run is displayed. */
  itemCount?: number;
}


/** Rounded-corner orthogonal polyline through `pts`. Pure. */
function roundedPolyline(pts: { x: number; y: number }[], r: number): string {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const len1 = Math.hypot(p1.x - p0.x, p1.y - p0.y) || 1;
    const len2 = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const d1 = Math.min(r, len1 / 2);
    const d2 = Math.min(r, len2 / 2);
    const a = { x: p1.x + ((p0.x - p1.x) / len1) * d1, y: p1.y + ((p0.y - p1.y) / len1) * d1 };
    const b = { x: p1.x + ((p2.x - p1.x) / len2) * d2, y: p1.y + ((p2.y - p1.y) / len2) * d2 };
    d += ` L ${a.x} ${a.y} Q ${p1.x} ${p1.y} ${b.x} ${b.y}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
}

/**
 * Orthogonal path for a BACK-EDGE (target's input is left of the source's
 * output). Each back-edge runs in its OWN horizontal CHANNEL so parallel
 * returns don't overlap:
 *   • target BELOW source (adjacent stacked nodes) → channel in the GAP
 *     between them — the tight n8n lateral bump.
 *   • target ABOVE source (the loop wrap-around return) → channel BELOW
 *     everything, so it clears the body and rises on the far left into the
 *     loop input, SEPARATE from the downward gap-bumps.
 * Returns `[svgPath, labelX, labelY]` (label on the channel run). Pure.
 */
export function backEdgePath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
): [string, number, number] {
  const stub = 28; // horizontal stub out of each handle
  const outX = sx + stub; // vertical leg, right of the source
  const inX = tx - stub; // vertical leg, left of the target
  const goingUp = ty < sy; // a wrap-around return (target above the source)
  const channelY = goingUp
    ? Math.max(sy, ty) + 90 // below everything (source is the lowest node here)
    : (sy + ty) / 2; // in the gap between the two stacked nodes
  const d = roundedPolyline(
    [
      { x: sx, y: sy },
      { x: outX, y: sy },
      { x: outX, y: channelY },
      { x: inX, y: channelY },
      { x: inX, y: ty },
      { x: tx, y: ty },
    ],
    14,
  );
  return [d, (outX + inX) / 2, channelY];
}


export function TrayActionsEdge(props: EdgeProps<TrayEdgeActionsData>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    data,
  } = props;

  // A BACK-EDGE (the loop node's body edging back into its own input) points
  // LEFT — the target's input handle sits left of the source's output. A plain
  // bezier cuts a diagonal across the canvas (the tangled loop). Route it as a
  // HAND-BUILT orthogonal rectangle UNDER the nodes: exit right of the source,
  // drop below everything, travel left, rise into the target's left input —
  // the tidy n8n loop-around. (getSmoothStepPath was unreliable for a
  // target-behind-source, drawing a diagonal.) Forward edges keep the bezier.
  const isBackEdge = targetX < sourceX - 8;
  let path: string;
  let labelX: number;
  let labelY: number;
  if (isBackEdge) {
    [path, labelX, labelY] = backEdgePath(sourceX, sourceY, targetX, targetY);
  } else {
    [path, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  }

  // The action group (+/trash) shows on hover. The trigger is the WHOLE edge
  // path, not just the tiny group box: an invisible wide stroke over the path
  // catches the hover and flips `hovered`. A short leave-delay bridges the gap
  // while the pointer travels from the path to the buttons (which sit at the
  // label point — for a back-edge that's the far bottom run, so without this
  // you could never reach them). Focus-within keeps keyboard discovery.
  const [hovered, setHovered] = React.useState(false);
  const hideTimer = React.useRef<number | null>(null);
  const show = React.useCallback(() => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setHovered(true);
  }, []);
  const hide = React.useCallback(() => {
    hideTimer.current = window.setTimeout(() => setHovered(false), 220);
  }, []);
  React.useEffect(
    () => () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    },
    [],
  );

  return (
    <>
      <BaseEdge path={path} markerEnd={markerEnd} style={style} />
      {/* Invisible wide hit-stroke over the whole edge — makes the ENTIRE
          path hoverable (not just the small action-group box), so the +/trash
          appear wherever you hover the edge, including a long loop back-edge. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        style={{ pointerEvents: "stroke", cursor: "pointer" }}
        onMouseEnter={show}
        onMouseLeave={hide}
      />
      <EdgeLabelRenderer>
        {/* n8n-style item-count pill — the number of items that flowed down
            this edge in the shown run. Sits just ABOVE the label point and
            hides on hover so it doesn't fight the +/trash action group.
            ``pointerEvents: none`` keeps the edge itself hoverable. */}
        {data?.itemCount !== undefined && !hovered && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "none",
            }}
            // NB: NO ``backdrop-blur`` here. This pill lives inside the
            // ReactFlow viewport (EdgeLabelRenderer), which transforms on every
            // zoom/pan; a backdrop-filter would force the browser to
            // re-rasterize the blurred region each frame → a heavy full-canvas
            // flicker that ONLY appeared after a run (the pills show item
            // counts). An opaque background is enough for legibility.
            className="whitespace-nowrap rounded bg-background px-1 text-[10px] tabular-nums leading-tight text-muted-foreground shadow-sm"
          >
            {data.itemCount === 1 ? "1 item" : `${data.itemCount} items`}
          </div>
        )}
        {/*
            ``pointerEvents: 'all'`` is mandatory: EdgeLabelRenderer
            wraps everything in a host div with pointer-events: none
            so the underlying edge stays draggable, so we re-enable
            events specifically for the action buttons.
        */}
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
          className="group/edge"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div
            className={cn(
              "flex items-center gap-1 rounded-md border border-border bg-surface/95 p-0.5 shadow-md",
              "transition-opacity",
              hovered ? "opacity-100" : "opacity-0",
              // Always show on focus-within so tab-keyboard discovery
              // works (the buttons inside the group will get focus).
              "focus-within:opacity-100",
            )}
          >
            <button
              type="button"
              title="Insert node here"
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded",
                "text-foreground hover:bg-accent hover:text-accent-foreground",
              )}
              onClick={(e) => {
                e.stopPropagation();
                data?.onInsertClick?.(id, { x: e.clientX, y: e.clientY });
              }}
            >
              <Plus className="h-3 w-3" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              title="Delete edge"
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded",
                "text-muted-foreground hover:bg-danger/10 hover:text-danger",
              )}
              onClick={(e) => {
                e.stopPropagation();
                data?.onDelete?.(id);
              }}
            >
              <Trash2 className="h-3 w-3" strokeWidth={2} />
            </button>
          </div>
          {/* (The earlier "wider transparent hit area" sibling was
              removed — being a later DOM sibling it stacked ABOVE the
              buttons and swallowed every click. ``opacity-0`` doesn't
              disable pointer events, so the visible action group at
              its real size still catches hover + click just fine.) */}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}


// ---------------------------------------------------------------------------
// Palette popover
// ---------------------------------------------------------------------------


export interface PalettePopoverProps {
  /** Where to anchor the popover (screen coords, e.g. from a mouse
   *  event). The popover renders at fixed position relative to the
   *  viewport, with a small offset to avoid covering the cursor. */
  position: { x: number; y: number };
  /** Catalog filter inputs — same shape as NodePalette. */
  catalog: CatalogNode[];
  workflowKind: WorkflowKind;
  direction?: Direction;
  /** Fired when the user picks a node kind. */
  onPick: (kind: NodeKind) => void;
  /** Fired on Escape / outside click. */
  onCancel: () => void;
}


// Same direction-filter as NodePalette — kept in lockstep so the
// popover offers exactly the same set of node kinds the sidebar does.
function isAllowedByDirection(node: CatalogNode, direction: Direction): boolean {
  switch (direction) {
    case "inbound":
      if (node.category === "sink") return false;
      if (node.kind === "read_canonical") return false;
      return true;
    case "internal":
      if (node.category === "source" || node.category === "sink") return false;
      return true;
    case "outbound":
      if (node.category === "source") return false;
      if (node.kind === "write_canonical") return false;
      return true;
  }
}

function isAllowedForKind(node: CatalogNode, kind: WorkflowKind): boolean {
  if (!node.kinds || node.kinds.length === 0) return true;
  return node.kinds.includes(kind);
}


const CATEGORY_LABEL: Record<string, string> = {
  source: "Sources",
  sink: "Sinks",
  transform: "Transforms",
  control: "Control flow",
  action: "Actions",
  io_canonical: "Canonical I/O",
  lookup: "Lookups",
  plugin: "Plugins",
};

const CATEGORY_ORDER = [
  "source",
  "sink",
  "transform",
  "control",
  "action",
  "io_canonical",
  "lookup",
  "plugin",
];


export function PalettePopover({
  position,
  catalog,
  workflowKind,
  direction,
  onPick,
  onCancel,
}: PalettePopoverProps) {
  const [query, setQuery] = React.useState("");
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on outside click + Escape.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onCancel();
    }
    window.addEventListener("keydown", onKey);
    // Use ``mousedown`` in the CAPTURE phase so we see the event
    // before ReactFlow's internal listeners call stopPropagation on
    // canvas / pane clicks. Without ``capture: true`` clicks on the
    // empty canvas area never reached this handler — the popover
    // only closed on Escape, which is what the user was hitting.
    window.addEventListener("mousedown", onClick, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick, true);
    };
  }, [onCancel]);

  // Filter + group the catalog.
  const grouped = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, CatalogNode[]>();
    catalog
      .filter((n) => isAllowedForKind(n, workflowKind))
      .filter((n) =>
        workflowKind === "canonical" && direction
          ? isAllowedByDirection(n, direction)
          : true,
      )
      .filter((n) => {
        if (!q) return true;
        return (
          n.label.toLowerCase().includes(q) ||
          n.kind.toLowerCase().includes(q) ||
          (n.description ?? "").toLowerCase().includes(q)
        );
      })
      .forEach((n) => {
        const arr = map.get(n.category) ?? [];
        arr.push(n);
        map.set(n.category, arr);
      });
    return map;
  }, [catalog, workflowKind, direction, query]);

  // Position the popover so it doesn't run off the right/bottom edge
  // of the viewport. Standard placement: 8px below-right of the click.
  const POPOVER_W = 280;
  const POPOVER_H = 380;
  const margin = 8;
  let x = position.x + margin;
  let y = position.y + margin;
  if (typeof window !== "undefined") {
    if (x + POPOVER_W > window.innerWidth - 8) {
      x = Math.max(8, window.innerWidth - POPOVER_W - 8);
    }
    if (y + POPOVER_H > window.innerHeight - 8) {
      y = Math.max(8, window.innerHeight - POPOVER_H - 8);
    }
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Insert node"
      style={{
        position: "fixed",
        left: x,
        top: y,
        width: POPOVER_W,
        maxHeight: POPOVER_H,
        zIndex: 50,
      }}
      className="flex flex-col overflow-hidden rounded-md border border-border bg-surface shadow-xl"
    >
      <div className="border-b border-border/60 p-2">
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter…"
          className="h-7 text-xs"
        />
      </div>
      <div className="flex-1 overflow-y-auto p-1">
        {CATEGORY_ORDER.map((cat) => {
          const nodes = grouped.get(cat);
          if (!nodes || nodes.length === 0) return null;
          return (
            <div key={cat} className="mb-2 last:mb-0">
              <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABEL[cat] ?? cat}
              </div>
              <ul>
                {nodes.map((n) => (
                  <li key={n.kind}>
                    <button
                      type="button"
                      onClick={() => onPick(n.kind)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs",
                        "hover:bg-accent/10",
                      )}
                      title={n.description ?? ""}
                    >
                      <NodeIconBadge
                        kind={n.kind}
                        category={n.category}
                        size={20}
                      />
                      <span className="flex-1 truncate font-medium">
                        {n.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        {Array.from(grouped.values()).every((v) => v.length === 0) && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No nodes match "{query}".
          </div>
        )}
      </div>
    </div>
  );
}
